import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ScrollText, Search } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { AUDIT_ACTIONS } from '@praxara/shared/constants';

interface AuditRow {
  id: string;
  action: string;
  resource: string;
  resourceId: string | null;
  description: string | null;
  createdAt: string;
  tenantId: string;
  user: { email: string; firstName: string; lastName: string; role: string } | null;
  tenant: { name: string; slug: string } | null;
}

export function PlatformAuditPage() {
  const [action, setAction] = useState('');
  const [tenantId, setTenantId] = useState('');

  const q = useQuery({
    queryKey: ['platform-admin', 'audit', action, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (action) params.set('action', action);
      if (tenantId) params.set('tenantId', tenantId);
      return (await api.get(`/api/platform-admin/audit?${params.toString()}`)).data;
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Cross-tenant audit</h1>
        <p className="text-sm text-muted-foreground">Last 100 entries across all tenants (most recent first).</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Action</label>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All actions</option>
            {AUDIT_ACTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Tenant ID</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="uuid or blank"
              className="w-80 pl-8 font-mono text-xs"
            />
          </div>
        </div>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !q.data?.items?.length ? (
        <EmptyState icon={ScrollText} title="No audit entries" description="Try removing filters." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {q.data.items.map((row: AuditRow) => (
                <div key={row.id} className="grid gap-1 px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="font-mono text-[10px]">{row.action}</Badge>
                    <span className="text-muted-foreground">on</span>
                    <code className="text-xs">{row.resource}</code>
                    {row.resourceId && <code className="text-[10px] text-muted-foreground">{row.resourceId.slice(0, 8)}...</code>}
                    <span className="ml-auto text-[10px] text-muted-foreground">{formatDate(row.createdAt)}</span>
                  </div>
                  {row.description && (
                    <div className="text-xs text-muted-foreground">{row.description}</div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    {row.tenant && <span>tenant: <span className="font-medium text-foreground">{row.tenant.name}</span> ({row.tenant.slug})</span>}
                    {row.user && (
                      <>
                        <span>·</span>
                        <span>by {row.user.firstName} {row.user.lastName} ({row.user.email})</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
